// Dashboard.jsx

import { Box, Grid, Typography, CircularProgress, Stack, Button, Skeleton, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Paper, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme/theme";

import MachineBarChart from "./MachineBarchart";
import ProductionPieChart from "./ProductionPieChart";
import useDashboardData from "./useDashboardData";
import SubscriptionCard from "./SubscriptionCard";


import RefreshIcon from "@mui/icons-material/Refresh";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useState } from "react";
import CustomTableContainer from "../../../Layout/CustomTableContainer";
import { PieChart } from "recharts";



export default function Dashboard() {

  const { data, loading, fetchdashboarddata, reelSummaryData } = useDashboardData();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  //const [fromDate, setFromDate] = useState(dayjs().subtract(7, "day"));
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [todayData, setTodayData] = useState(true);

  // if (loading || !data) {
  //   return (
  //     <Box p={4} display="flex" justifyContent="center" alignItems="center">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const startDate = data?.subscription?.start_date
    ? new Date(data.subscription.start_date)
    : null;

  const expiryDate = data?.subscription?.expiry_date
    ? new Date(data.subscription.expiry_date)
    : null;

  let daysLeft = 0;
  let totalDays = 1; // avoid dividing by zero

  if (startDate && expiryDate && !isNaN(startDate) && !isNaN(expiryDate)) {
    const now = new Date();
    daysLeft = Math.max(
      0,
      Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
    );

    totalDays = Math.ceil((expiryDate - startDate) / (1000 * 60 * 60 * 24));
  }



  const fetchDashBoardTopDetails = async () => {
    // Trim to avoid spaces being considered valid
    const from = (fromDate || '');
    const to = (toDate || '');

    // 1. Required checks
    if (!from && !to) {
      alert('Please select both From date and To date.');
      return;
    }

    if (!from) {
      alert('Please select From date.');
      return;
    }

    if (!to) {
      alert('Please select To date.');
      return;
    }

    // 2. Basic date validity checks
    const fromDt = new Date(from);
    const toDt = new Date(to);



    if (isNaN(fromDt.getTime())) {
      alert('From date is not a valid date.');
      return;
    }

    if (isNaN(toDt.getTime())) {
      alert('To date is not a valid date.');
      return;
    }

    // 3. Logical range validation
    if (fromDt > toDt) {
      alert('From date cannot be later than To date.');
      return;
    }

    // 4. Optional: prevent future dates (if your use case requires it)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // if (fromDt > today || toDt > today) {
    //   alert('Dates cannot be in the future.');
    //   return;
    // }

    // 5. If everything is valid, fetch data

    const para_from = dayjs(from).format('YYYY-MM-DD');
    const para_to = dayjs(to).format('YYYY-MM-DD');

    if (para_from === para_to) {
      setTodayData(true);
    }
    else {
      setTodayData(false);
    }
    fetchdashboarddata(para_from, para_to);
  };



  return (
    <Box p={3}>
      {/* <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
        Dashboard
      </Typography> */}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Dashboard
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" marginBottom={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(newVal) => setFromDate(newVal)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: 150 } // adjust size here
                }
              }}
            />

            <DatePicker
              label="To"
              value={toDate}
              onChange={(newVal) => setToDate(newVal)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: 150 } // adjust size here
                }
              }}
            />
          </LocalizationProvider>


          <Button
            variant="contained"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchDashBoardTopDetails?.()}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Grid container spacing={3}>
          {/* Subscription Skeleton */}
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
          </Grid>

          {/* Production KPI Cards Skeleton */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {Array(6).fill(0).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height="110px"
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Sales Skeleton */}
          <Grid item xs={12} md={4}>
            <Skeleton
              variant="rectangular"
              height="120px"
              sx={{ borderRadius: 3 }}
            />
          </Grid>

          {/* Warehouse KPI Skeleton */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {Array(3).fill(0).map((_, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height="120px"
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Charts Skeleton */}
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      ) : (
        // Your original Grid content here
        <Grid container spacing={3}>

          {/* Subscription */}
          <Grid item xs={12} md={3} xl={2}>
            <SubscriptionCard data={data} colors={colors} daysLeft={daysLeft} totalDays={totalDays} />
          </Grid>

          {/* Production KPI Cards */}
          <Grid item xs={12} md={9} xl={10}>
            <Grid container spacing={2}>
              {[
                {
                  title: todayData ? "MO Released Today" : "Total MO Released",
                  value: data.production.mo_released_today,
                  bg: colors.primary[400],
                  text: colors.grey[100],
                },
                {
                  title: "Planned Quantity",
                  value: data.production.planned_qty,
                  bg: colors.greenAccent[600],
                  text: colors.grey[900],
                },
                {
                  title: "Produced Parent Reels",
                  value: data.production.produced_reels,
                  bg: colors.orangeAccent[500],
                  text: colors.grey[900],
                },
                {
                  title: "Produced Child Reels",
                  value: data.production.produced_child_reels,
                  bg: colors.redAccent[400],
                  text: colors.grey[900],
                },
                {
                  title: "Produced Reams",
                  value: data.production.produced_reams,
                  bg: colors.purpleAccent[500],
                  text: colors.grey[100],
                },
                {
                  title: "Total Bundles",
                  value: data.production.produced_bundle,
                  bg: colors.blueAccent[400],
                  text: colors.grey[900],
                },
                {
                  title: "Total Ream Pallet",
                  value: data.production.produced_ream_pallet,
                  bg: colors.yellowAccent[600],
                  text: colors.grey[900],
                },
                // {
                //   title: "Bundle / Ream Pallet",
                //   value: data.production.produced_bundle_pallet,
                //   bg: colors.yellowAccent[600],
                //   text: colors.grey[900],
                // },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={3} xl={3} key={index}>
                  <Box
                    sx={{
                      backgroundColor: item.bg,
                      color: item.text,
                      borderRadius: 3,
                      padding: 2.5,
                      boxShadow: 3,
                      height: "110px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      transition: "all 0.25s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.04)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, mt: 1, letterSpacing: 0.5 }}
                    >
                      {item.value ?? 0}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              {/* <Grid item xs={12} sm={6} md={4} >
                <Paper sx={{ padding: 2, borderRadius: 3 }}>
                  <Typography variant="h6" mb={2}>Grade Wise Reel Summary</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: colors.primary[400] }}>
                          <TableCell>Grade</TableCell>
                          <TableCell align="right">Reel Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reelSummaryData.map((s) => (
                          <TableRow key={s.grade} hover>
                            <TableCell>{s.grade}</TableCell>
                            <TableCell align="right">{s.reelcount.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid> */}
              {/* 
              <Grid item xs={12} sm={6} md={4}>
                <Accordion sx={{ backgroundColor: colors.blueAccent[800] }} defaultExpanded>
                  <AccordionSummary>Child Reel Summary - {reelSummaryData.length} grades</AccordionSummary>

                  <AccordionDetails>
                    <Table size="small">
                      <CustomTableContainer colors={colors}>
                        <TableContainer>
                          <Table>
                            <TableHead sx={{ height: 10, backgroundColor: colors.primary[400] }}>
                              <TableRow>
                                <TableCell>Grade</TableCell>
                                <TableCell>Reel Count</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {reelSummaryData.map((s) => (

                                <TableRow>
                                  <TableCell>{s.grade}</TableCell>
                                  <TableCell>{s.reelcount}</TableCell>
                                </TableRow>

                              ))}

                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CustomTableContainer>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              </Grid> */}




              {/* <Grid item xs={12} sm={6} md={4} >
                <Box
                  sx={{
                    backgroundColor: colors.blueAccent[400],
                    color: colors.grey[900],
                    borderRadius: 3,
                    padding: 2.5,
                    boxShadow: 3,
                    height: "110px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.04)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CustomTableContainer colors={colors}>
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ height: 10, backgroundColor: colors.primary[400] }}>
                          <TableRow>
                            <TableCell>Grade</TableCell>
                            <TableCell>Reel Count</TableCell>                           
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reelSummaryData.map((s) => (
                            
                              <TableRow>
                                <TableCell>{s.grade}</TableCell>
                                <TableCell>{s.reelcount}</TableCell>                               
                              </TableRow>
                           
                          ))}

                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CustomTableContainer>

                </Box>
              </Grid> */}

            </Grid>
          </Grid>


          {/* Sales & Warehouse */}
          <Grid item xs={12} md={3} xl={2}>
            <Box
              sx={{
                backgroundColor: colors.redAccent[500],
                color: colors.grey[100],
                borderRadius: 3,
                padding: 2.5,
                boxShadow: 3,
                height: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                transition: "all 0.25s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "scale(1.04)",
                  boxShadow: 6,
                },
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {todayData ? "New Orders Today" : "Total Orders"}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                {data.sales.new_orders_today ?? 0}
              </Typography>
            </Box>
          </Grid>

          {/* RIGHT SIDE – 3 KPI BOXES */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              {/* Reels Received */}
              <Grid item xs={12} sm={4} md={3} xl={4}>
                <Box
                  sx={{
                    backgroundColor: colors.blueAccent[500],
                    color: colors.grey[100],
                    borderRadius: 3,
                    padding: 2.5,
                    boxShadow: 3,
                    height: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.04)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {todayData ? "Reels Received Today" : "Reels Received"}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {data.warehouse.reels_received ?? 0}
                  </Typography>
                </Box>
              </Grid>

              {/* Bundles Received */}
              <Grid item xs={12} sm={4} md={3} xl={4}>
                <Box
                  sx={{
                    backgroundColor: colors.greenAccent[500],
                    color: colors.grey[900],
                    borderRadius: 3,
                    padding: 2.5,
                    boxShadow: 3,
                    height: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.04)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {todayData ? "Bundles Received Today" : "Bundles Received"}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {data.warehouse.bundles_received ?? 0}
                  </Typography>
                </Box>
              </Grid>

              {/* Ream Pallets */}
              <Grid item xs={12} sm={4} md={3} xl={4}>
                <Box
                  sx={{
                    backgroundColor: colors.primary[400],
                    color: colors.grey[100],
                    borderRadius: 3,
                    padding: 2.5,
                    boxShadow: 3,
                    height: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.04)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {todayData ? "Ream Pallets Today" : "Ream Pallets Received"}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {data.warehouse.ream_pallet_received ?? 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>



          <Grid item xs={12} md={2}>
            <Accordion sx={{ backgroundColor: colors.greenAccent[800] }} defaultExpanded>
              <AccordionSummary>Child Reel Summary ( {reelSummaryData.length} grades)</AccordionSummary>

              <AccordionDetails>
                <Table size="small">
                  <CustomTableContainer colors={colors}>
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ height: 10, backgroundColor: colors.primary[400] }}>
                          <TableRow>
                            <TableCell>Grade</TableCell>
                            <TableCell>Reel Count</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reelSummaryData.map((s) => (

                            <TableRow>
                              <TableCell>{s.grade}</TableCell>
                              <TableCell>{s.reelcount}</TableCell>
                            </TableRow>

                          ))}

                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CustomTableContainer>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <MachineBarChart data={data.machine_production} todayData={todayData} />
          </Grid>

          <Grid item xs={12} md={4}>
            <ProductionPieChart production={data.production} todayData={todayData} />
          </Grid>
        </Grid>
      )}


    </Box>
  );
}
